import { Client, IMessage, StompSubscription } from "@stomp/stompjs";

/**
 * La conexión STOMP: por acá entran los mensajes del chat y las notificaciones sin que la pantalla
 * tenga que preguntar cada tantos segundos.
 *
 * <p>Es un singleton con una sola conexión para toda la app. Cada pantalla se suscribe a lo suyo y
 * se desuscribe al desmontarse; el socket queda abierto mientras dure la sesión, porque abrir uno
 * por componente multiplica los handshakes y deja sesiones colgadas en el backend cada vez que el
 * usuario cambia de pantalla.
 */
class WsService {

    private client?: Client;

    private conectado = false;

    /**
     * Suscripciones pedidas antes de que el socket estuviera listo.
     *
     * <p>Sin esto se pierden: la pantalla monta y se suscribe en el mismo tick en que arranca la
     * conexión, así que en el primer render el cliente todavía no está conectado y la llamada a
     * `subscribe` explotaría. Se guardan y se aplican en el onConnect — que además es el que corre
     * de nuevo en cada reconexión, así que también es lo que hace que el chat siga vivo después de
     * un corte de red.
     */
    private pendientes: { destino: string; handler: (msg: any) => void; sub?: StompSubscription }[] = [];

    /** Abre la conexión. Es idempotente: llamarla dos veces no abre dos sockets. */
    connect = (token: string) => {
        if (this.client && this.client.active) return;

        this.client = new Client({
            brokerURL: this.brokerUrl(),
            connectHeaders: { Authorization: `Bearer ${token}` },
            reconnectDelay: 5000,
            heartbeatIncoming: 10000,
            heartbeatOutgoing: 10000,
            onConnect: () => {
                this.conectado = true;
                // Al reconectar hay que rehacer TODAS las suscripciones: el broker del otro lado no
                // recuerda nada de la sesión anterior.
                this.pendientes.forEach((p) => {
                    p.sub = this.suscribir(p.destino, p.handler);
                });
            },
            onDisconnect: () => {
                this.conectado = false;
            },
            onWebSocketClose: () => {
                this.conectado = false;
            },
            onStompError: (frame) => {
                console.error("[WS] error STOMP:", frame.headers["message"]);
            },
        });

        this.client.activate();
    };

    disconnect = () => {
        this.pendientes = [];
        this.conectado = false;
        this.client?.deactivate();
        this.client = undefined;
    };

    get isConnected(): boolean {
        return this.conectado;
    }

    /**
     * Escucha un destino. Devuelve la función para dejar de escucharlo, que es lo que el
     * `useEffect` de la pantalla tiene que devolver.
     */
    subscribe = (destino: string, handler: (msg: any) => void): (() => void) => {
        const entrada: { destino: string; handler: (msg: any) => void; sub?: StompSubscription } = {
            destino,
            handler,
        };
        this.pendientes.push(entrada);

        if (this.conectado) {
            entrada.sub = this.suscribir(destino, handler);
        }

        return () => {
            this.pendientes = this.pendientes.filter((p) => p !== entrada);
            try {
                entrada.sub?.unsubscribe();
            } catch {
                /* el socket ya se cayó: la suscripción murió con él */
            }
        };
    };

    private suscribir = (destino: string, handler: (msg: any) => void) =>
        this.client!.subscribe(destino, (msg: IMessage) => {
            try {
                handler(JSON.parse(msg.body));
            } catch {
                console.warn("[WS] no se pudo parsear el mensaje de", destino);
            }
        });

    /**
     * La URL absoluta del socket.
     *
     * <p>El cliente STOMP necesita una absoluta (ws:// o wss://), así que hay que resolver
     * REACT_APP_API_URL, que puede venir de tres formas: vacía (mismo origen), relativa, o absoluta
     * con host y puerto (que es el caso de desarrollo, con el backend en :8080). Concatenar sin este
     * paso deja un brokerURL inválido y la conexión falla en silencio salvo por el error de STOMP.
     */
    private brokerUrl = (): string => {
        const api = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
        const absoluta = /^https?:\/\//i.test(api) ? api : window.location.origin + api;
        return absoluta.replace(/^http/, "ws") + "/ws";
    };
}

const wsService = new WsService();

export default wsService;
